import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServiceNavigation from "@/components/ServiceNavigation";
import { motion } from "framer-motion";
import { Play, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import LeadFormModal from "@/components/LeadFormModal";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const getYouTubeVideoId = (url: string) => {
  if (url.includes('youtu.be/')) return url.split('youtu.be/')[1]?.split('?')[0];
  if (url.includes('/shorts/')) return url.split('/shorts/')[1]?.split('?')[0];
  if (url.includes('watch?v=')) return url.split('watch?v=')[1]?.split('&')[0];
  return null;
};

const getYouTubeThumbnail = (url: string, quality: 'maxres' | 'hq' | 'mq' | 'sd' = 'hq') => {
  const videoId = getYouTubeVideoId(url);
  const qualityMap = { maxres: 'maxresdefault', hq: 'hqdefault', mq: 'mqdefault', sd: 'sddefault' };
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
};

const getEmbedUrl = (url: string) => {
  const videoId = getYouTubeVideoId(url);
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&controls=1`;
};

const videos = [
  { id: 1, youtubeUrl: "https://youtube.com/shorts/rTb5u8ADGyA", title: "Solemn - Réal Béland" },
  { id: 2, youtubeUrl: "https://youtube.com/shorts/8kK14DRXZOw", title: "MREX - Plus haute tour" },
  { id: 3, youtubeUrl: "https://youtube.com/shorts/tL0i9YTqPi0", title: "MREX - Olivier Lemieux" },
  { id: 4, youtubeUrl: "https://youtube.com/shorts/v1pNW2JvEbk", title: "Alex G - Nascar" },
];

const features = [
  { text: "Plan de contenu avec hooks puissants" },
  { text: "Tournage rapide et structuré" },
  { text: "Montage dynamique avec sous-titres stylisés" },
  { text: "Calendrier de publication optimisé" },
  { text: "Option repurposing de contenu existant" },
];

const reelsSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Création de Reels Instagram et TikTok",
  "serviceType": "Production de vidéos courtes pour réseaux sociaux",
  "description": "Création de Reels Instagram et TikTok percutants pour entreprises B2B. Hooks puissants, montage dynamique et stratégie de publication optimisée.",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Agence 8020",
    "address": { "@type": "PostalAddress", "addressLocality": "Montréal", "addressRegion": "QC", "addressCountry": "CA" },
    "url": "https://agence8020.com"
  },
  "areaServed": { "@type": "Place", "name": "Québec, Canada" }
};

const ReelsPage = () => {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Création de Reels performants pour entreprises B2B"
        description="Des reels stratégiques pensés pour générer visibilité, leads et revenus pour les entreprises B2B."
        canonical="/reels"
        schemaJson={reelsSchema}
      />

      <main className="min-h-screen bg-background overflow-hidden">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-8 relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-primary/20 to-transparent opacity-50 blur-[120px] rounded-full" />
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Play className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Vidéos courtes</span>
              </div>
              <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6">
                Reels Instagram <br className="hidden md:block" />
                <span className="text-primary">& TikTok</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Des vidéos qui arrêtent le scroll et génèrent des résultats concrets pour votre entreprise.
              </p>
            </motion.div>
          </div>
        </section>

        <ServiceNavigation />

        {/* Video Carousel */}
        <section className="py-12">
          <div className="container mx-auto px-4 sm:px-6">
            <Carousel opts={{ align: "center", loop: true }} className="w-full">
              <div className="flex justify-end mb-6 gap-2 px-2">
                <CarouselPrevious className="relative inset-0 translate-x-0 translate-y-0 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/20 hover:bg-primary border-none text-foreground" />
                <CarouselNext className="relative inset-0 translate-x-0 translate-y-0 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/20 hover:bg-primary border-none text-foreground" />
              </div>
              <CarouselContent className="-ml-2 sm:-ml-4">
                {videos.map((video, index) => (
                  <CarouselItem key={video.id} className="pl-2 sm:pl-4 basis-[55%] xs:basis-[50%] sm:basis-[45%] md:basis-[300px]">
                    <motion.div
                      className="relative aspect-[9/16] rounded-2xl sm:rounded-3xl overflow-hidden cursor-pointer group"
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => setSelectedVideo(video.youtubeUrl)}
                      whileHover={{ scale: 1.02, y: -8 }}
                    >
                      <img
                        src={getYouTubeThumbnail(video.youtubeUrl)}
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (!target.src.includes('sddefault')) {
                            target.src = getYouTubeThumbnail(video.youtubeUrl, 'sd');
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
                      <div className="absolute top-3 left-3 right-3 sm:top-4 sm:left-4 sm:right-4">
                        <div className="flex items-center gap-2 text-white/90">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Play className="w-3 h-3 sm:w-4 sm:h-4 fill-white" />
                          </div>
                          <span className="text-xs sm:text-sm font-medium line-clamp-1">{video.title}</span>
                        </div>
                      </div>
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center"
                        initial={{ opacity: 0.8 }}
                        animate={{ opacity: hoveredIndex === index ? 1 : 0.8 }}
                      >
                        <motion.div
                          className="w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-[#FF0000] flex items-center justify-center shadow-2xl"
                          animate={{ 
                            scale: hoveredIndex === index ? 1.15 : 1,
                            boxShadow: hoveredIndex === index ? "0 0 40px rgba(255,0,0,0.5)" : "0 10px 30px rgba(0,0,0,0.3)"
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white fill-white ml-1" />
                        </motion.div>
                      </motion.div>
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              >
                Des vidéos qui arrêtent le <span className="text-primary">scroll.</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-xl text-muted-foreground leading-relaxed"
              >
                Créer des reels, ce n'est pas juste filmer avec son cell et publier n'importe quand.
                <br />
                <span className="text-foreground font-medium">C'est savoir quoi dire, comment le dire… et comment captiver en 3 secondes.</span>
              </motion.p>
            </div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mb-16"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-5 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/30 hover:bg-card/80 transition-all duration-300 group"
                  whileHover={{ scale: 1.02, y: -2 }}
                >
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Highlight Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <div className="p-8 rounded-3xl bg-primary/10 relative overflow-hidden border border-primary/20">
                <div className="absolute inset-0 bg-background/80" />
                <div className="relative z-10 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <p className="text-lg text-foreground leading-relaxed">
                    Chez Agence 8020, on crée des Reels pensés pour générer de la visibilité, des DM, des leads et du cashflow.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-primary/5 via-transparent to-transparent" />
          <div className="container mx-auto px-6 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Vous voulez des <span className="text-primary">Reels</span> qui génèrent des résultats?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Parlez-nous de votre projet. On vous répond en 24h.
              </p>
              <Button 
                variant="hero" 
                size="xl" 
                className="shadow-neon group"
                onClick={() => setIsLeadModalOpen(true)}
              >
                Parlons de votre projet
                <motion.span animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>→</motion.span>
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Video Modal */}
        <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
          <DialogContent className="p-0 bg-black border-none overflow-hidden rounded-3xl max-w-[400px] aspect-[9/16]">
            {selectedVideo && (
              <iframe
                src={getEmbedUrl(selectedVideo)}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </DialogContent>
        </Dialog>

        <LeadFormModal isOpen={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} />
        <Footer />
      </main>
    </>
  );
};

export default ReelsPage;